"use client";

import React, { useEffect, useRef, useState } from "react";
import { Loader, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { cn } from "@/lib/utils";
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
} = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="6568" className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="mb-6 rounded-full bg-primary/20 p-4 text-primary">
          <CheckCircle2 className="size-12" />
        </div>
        <h3 className="text-2xl font-semibold text-foreground mb-2">
          Request Received
        </h3>
        <p className="text-muted-foreground max-w-xs mx-auto mb-8">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-primary text-primary hover:bg-primary/10">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-xs uppercase tracking-widest text-muted-foreground/80">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Jane Smith" className="bg-transparent border-input rounded-lg text-[18px] font-sans h-12 focus-visible:ring-primary focus-visible:border-primary transition-colors" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Business email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid business email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-xs uppercase tracking-widest text-muted-foreground/80">
                Business Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@company.com" className="bg-transparent border-input rounded-lg text-[18px] font-sans h-12 focus-visible:ring-primary focus-visible:border-primary transition-colors" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-xs uppercase tracking-widest text-muted-foreground/80">
                Company Phone
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-transparent border-input rounded-lg text-[18px] font-sans h-12 focus-visible:ring-primary focus-visible:border-primary transition-colors" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] uppercase tracking-wider opacity-60">
                Optional – for immediate scheduling
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-xs uppercase tracking-widest text-muted-foreground/80">
                Solution Interest
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g., Enterprise Platform" className="bg-transparent border-input rounded-lg text-[18px] font-sans h-12 focus-visible:ring-primary focus-visible:border-primary transition-colors" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="message" rules={{
      required: "Please provide a brief description of your needs",
      minLength: {
        value: 10,
        message: "Please provide more details (min 10 chars)"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-xs uppercase tracking-widest text-muted-foreground/80">
              Demo Requirements
            </FormLabel>
            <FormControl>
              {}
              <textarea className={cn("flex min-h-[120px] w-full rounded-lg border border-input bg-transparent px-3 py-2 text-[18px] shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 resize-y transition-all", "font-sans tracking-normal")} placeholder="Tell us about your specific goals for this demo..." {...field} />
            </FormControl>
            <FormDescription className="text-[10px] uppercase tracking-wider opacity-60">
              Include any specific features you wish to see
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3 text-destructive animate-in fade-in slide-in-from-top-2">
          <AlertCircle className="size-5 shrink-0 mt-0.5" />
          <p className="text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className={cn("w-full rounded-lg h-14 text-lg font-medium transition-all duration-600 ease-in-out", "bg-primary text-primary-foreground hover:bg-primary/90", "hover:scale-[1.01] active:scale-[0.99] shadow-md hover:shadow-xl")}>
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : <>
            Submit Request
            <Send className="ml-2 size-4 opacity-80" />
          </>}
      </Button>
    </Form>;
}
export default function ContactForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="request-demo-form" className="relative w-full py-20 md:py-28 bg-white overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-primary/5 blur-[100px]" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div ref={ref} className={cn("max-w-2xl mx-auto transition-all duration-1000 ease-out transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <Card className="border border-white/10 bg-white/80 rounded-[10px] shadow-[0_20px_25px_-5px_rgba(0,0,0,0.05)] backdrop-blur-md">
            <CardHeader className="text-center pb-2 pt-10">
              <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Send className="h-7 w-7" />
              </div>
              <CardTitle className="text-4xl font-sans font-semibold tracking-tight text-foreground">
                Request a Live Demo
              </CardTitle>
              <CardDescription className="text-muted-foreground mt-3 text-lg font-sans">
                Experience our enterprise platform in action. Fill out the form below to schedule your personalized session.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 md:p-12 pt-8">
              <FormProvider formId="request_demo_contact" sectionName="demo-request-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Submit Request" successMessage="Thank you! Your demo request has been received. Our team will contact you shortly to schedule a time." errorMessage="We encountered an error processing your request. Please try again or contact support.">
                <ContactFormContent />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}